import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowLeft, Loader2, AlertTriangle, Handshake, Award } from 'lucide-react';
import { toast } from 'sonner';

const AffiliateLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (user.is_admin) navigate('/admin', { replace: true });
    else if (user.role === 'affiliate') navigate('/profile', { replace: true });
    else if (user.verification_status === 'approved') navigate('/profile', { replace: true });
    else navigate('/verification', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back, partner!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Something went wrong. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white flex-col p-12 overflow-hidden">
        <div className="absolute -top-20 -right-10 w-80 h-80 bg-orange-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1200&q=70" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/60 to-slate-950/90" />
        </div>

        <div className="relative z-10">
          <Link to="/affiliate-hub" className="inline-flex items-center gap-2.5 mb-12">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-9 w-auto" />
            <span className="font-bold text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/15 border border-orange-500/30 rounded-full text-xs font-semibold text-orange-300 mb-6 tracking-wider uppercase">
            <Award className="h-3.5 w-3.5" /> Affiliate Marketplace
          </div>
        </div>

        <div className="relative z-10 mt-auto">
          <h2 className="text-3xl font-bold leading-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Welcome back, <span className="text-orange-400">verified partner</span>.
          </h2>
          <p className="text-slate-300 text-sm leading-relaxed mb-6 max-w-md">
            Sign in to manage your offers, track referrals, and connect with the box truck community.
          </p>
          <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur max-w-sm">
            <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center"><Handshake className="h-5 w-5 text-white" /></div>
            <div>
              <p className="text-xs font-bold text-white">Verified Partners</p>
              <p className="text-[11px] text-slate-400">Real Resources. Real Earnings.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 bg-white">
        <div className="w-full max-w-[440px]">
          <Link to="/affiliate-hub" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors" data-testid="affiliate-login-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to Affiliate Hub
          </Link>

          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </div>

          <span className="inline-block text-xs font-semibold text-orange-600 uppercase tracking-wider mb-2">Affiliate Sign In</span>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Partner portal</h2>
          <p className="text-slate-500 text-sm mb-8">Access your Verified Partner account.</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3.5 mb-6 flex items-start gap-3" data-testid="affiliate-login-error">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="affiliate-login-form">
            <div>
              <label htmlFor="aff-email" className="block text-sm font-medium text-slate-900 mb-2">Email address</label>
              <input id="aff-email" type="email" autoComplete="username email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="partner@company.com" className="w-full h-12 px-4 rounded-md border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow" data-testid="affiliate-login-email" />
            </div>
            <div>
              <label htmlFor="aff-password" className="block text-sm font-medium text-slate-900 mb-2">Password</label>
              <div className="relative">
                <input id="aff-password" type={showPw ? 'text' : 'password'} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password" className="w-full h-12 px-4 pr-12 rounded-md border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow" data-testid="affiliate-login-password" />
                <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1" data-testid="affiliate-login-toggle-pw">
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30 hover:shadow-orange-500/40" data-testid="affiliate-login-submit">
              {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Signing in...</> : 'Sign In Affiliate'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-8">
            New to the marketplace?{' '}
            <Link to="/affiliate/join" className="font-semibold text-orange-600 hover:text-orange-700 hover:underline underline-offset-4" data-testid="affiliate-login-join-link">Apply as a partner</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AffiliateLogin;
